import { createReducer } from '@reduxjs/toolkit';
import { merge } from 'lodash-es';
import { State } from 'state';
import * as actions from '../actions';
import { setPageLoading } from '../actions';
import {
  Appearance,
  Folder,
  IAdvancedFilters,
  IModerationMessagesState,
  IPageInfo,
  Message,
  MessagePublishedStatus,
  MessagesCounts,
  MessageType,
  ModeratedBy,
  ModerationSearchType,
  Sorting,
  SpotPolicy,
  ViewType
} from '../types';
import { LocalStorageItems } from '../../../../../../constants';
import { setHasMorePending, setHasMorePendingStatus } from '../actions/commentsActions';

const TOTAL_ITEMS_ON_PAGE = 20;

type TModerationReducer = ReturnType<typeof moderationReducer>;

const {
  setPageInfo: setPageInfoAction,
  getSpotItemsStart,
  getSpotItemsSuccess,
  getSpotItemsFailure,
  setFolderCount,
  toggleItem,
  selectAllItems,
  clearAllItems,
  resetAllFilters,
  resetModerationFilters,
  approveItemsStart,
  approveItemsSuccess,
  approveItemsFailure,
  rejectItemsStart,
  rejectItemsSuccess,
  rejectItemsFailure,
  setClientAppearance,
  searchModerationItemsSuccess,
  searchModerationItemsFailure,
  changePageFilters,
  toggleModerationModeAction,
  fetchMessageTagsSpansSuccess,
  fetchHighlightTagsConfig
} = actions;

const defaultPageInfo: IPageInfo = {
  viewType: ViewType.SPOT,
  hasNext: false,
  offset: 0,
  folder: Folder.PENDING,
  tag: ['all'],
  sortBy: Sorting.NEWEST,
  messageType: MessageType.ALL,
  published: MessagePublishedStatus.ALL,
  moderatedBy: ModeratedBy.ALL
};

const defaultAdvancedFilters: IAdvancedFilters = {
  user: '',
  ip: '',
  conversation: '',
  text: '',
  message_id: ''
};

const defaultCounts: MessagesCounts = {
  all: 0,
  pending: 0,
  blocked: 0,
  approved: 0
};

const initialState: IModerationMessagesState = {
  itemsList: [],
  lockedItems: [],
  pageInfo: {
    ...defaultPageInfo,
    ...JSON.parse(localStorage.getItem(LocalStorageItems.ModerationFilterCriteria))
  },
  isLoading: false,
  itemsOnPage: TOTAL_ITEMS_ON_PAGE,
  isSpotItemsFetched: false,
  activeItems: 0,
  total: 0,
  isModerationMode: false,
  counts: defaultCounts,
  filters: defaultAdvancedFilters,
  lastFetch: new Date().getTime() / 1000,
  isMultiSelect: false,
  isEmptyResults: false,
  spotPolicy: SpotPolicy.APPROVE_ALL,
  expirationTime: null,
  unpublishedMessagesOffset: 0,
  isHighlightTagsEnabled: true,
  hasMorePending: false,
  hasMorePendingStatus: 'none',
  sessionModeratedMessageCount: 0
};

const moderationReducer = createReducer(initialState, {
  [setPageInfoAction.type]: (state, { payload: { pageInfo } }) => {
    if (!pageInfo) {
      setPageInfo(state, defaultPageInfo);
      resetState(state);
    } else {
      const ignoreLoading =
        state.isModerationMode &&
        pageInfo.folder === undefined &&
        state.pageInfo.folder === Folder.PENDING &&
        (pageInfo.sortBy !== undefined || pageInfo.tag !== undefined);

      // setting isLoading to true triggers items fetch, in case of moderation mode on we don't want to fetch items
      if (!ignoreLoading) {
        state.isLoading = true;
      }

      // TODO: turning off this here should also turn off MAB.
      state.isMultiSelect = false;
      if (pageInfo?.folder && pageInfo?.folder !== Folder.PENDING) {
        state.unpublishedMessagesOffset = 0;
      }
      if (pageInfo?.sortBy) {
        state.unpublishedMessagesOffset = 0;
      }
      state.unpublishedMessagesOffset = getUnpublishedMessagesOffset(pageInfo.offset, state);

      if (state.isModerationMode) {
        const currentFolder = state.pageInfo.folder;
        const nextFolder = pageInfo.folder;

        // If we're leaving the pending folder, save the locked items
        if (currentFolder === Folder.PENDING && nextFolder !== Folder.PENDING) {
          state.lockedItems = [...state.itemsList];
        }

        // If we're entering the pending folder, restore the locked items
        if (nextFolder === Folder.PENDING) {
          state.itemsList = [...state.lockedItems];
          state.activeItems = calculateActiveItems(state);
        }
      }

      setPageInfo(state, pageInfo);
    }
  },
  [setPageLoading.type]: (state, { payload }) => {
    state.isLoading = payload;
  },
  [getSpotItemsStart.type]: (state, { payload: { start, viewType } }) => {
    state.isLoading = true;
    setPageInfo(state, { viewType });

    if (start) {
      resetState(state);
    }
  },
  [getSpotItemsSuccess.type]: (state, { payload: { messages, folder, expirationTime, spotPolicy } }) => {
    setItems(state, { folder, messages, expirationTime });
    state.isLoading = false;
    state.isSpotItemsFetched = true;

    // TODO: In the future we'll have to move spotPolicy to spot state module.
    state.spotPolicy = SpotPolicy[spotPolicy?.toUpperCase()] ?? state.spotPolicy;
    if (folder === Folder.PENDING) {
      setLastFetch(state);
    }
  },
  [getSpotItemsFailure.type]: (state, { payload: { error } }) => {
    state.isLoading = false;
  },
  // Set the total number of items on the folder based on the pageInfo (not realtime counter).
  [setFolderCount.type]: (state, { payload: { folderItemsCount, folder = state.pageInfo.folder } }) => {
    /**
     * TODO: Temporary - remove when BED issues will be resolved.
     * Currently result contains resolved messages.
     */
    const hasNext = folderItemsCount > state.itemsList.length;
    state.counts = { ...state.counts, [folder]: folderItemsCount };
    state.total = folderItemsCount;
    setPageInfo(state, { hasNext });
  },
  [toggleItem.type]: (state, { payload: { id, checked } }) => {
    state.itemsList = state.itemsList.map((item: Message) =>
      item.id === id ? updateItemSelectionState(item, checked) : item
    );

    // If it's the last item checked, turn off the multi select
    state.isMultiSelect = checked || isSelectedItemExists(state);
  },
  [selectAllItems.type]: state => {
    state.itemsList = state.itemsList.map((item: Message) => {
      if (item.clientAppearance === Appearance.COLLAPSED) {
        return item;
      } else {
        return updateItemSelectionState(item, true);
      }
    });
    state.isMultiSelect = true;
  },
  [clearAllItems.type]: state => {
    state.itemsList = state.itemsList.map((item: Message) => updateItemSelectionState(item, false));
    state.isMultiSelect = false;
  },
  [resetAllFilters.type]: (state, { payload: { pageInfo = {}, filters = {}, isLoading = true } }) => {
    // You can provide pageInfo and filters as overrides per use case
    setPageInfo(state, merge(defaultPageInfo, pageInfo));
    setAdvancedFilters(state, merge(defaultAdvancedFilters, filters));
    resetState(state);
    localStorage.removeItem(LocalStorageItems.ModerationFilterCriteria);
    state.isModerationMode = false;
    state.isLoading = isLoading;
  },
  [resetModerationFilters.type]: state => {
    setPageInfo(state, {
      tag: defaultPageInfo.tag,
      messageType: defaultPageInfo.messageType,
      published: defaultPageInfo.published
    });
  },
  [setClientAppearance.type]: (state, { payload: { id, appearance, folder } }) => {
    const index = state.itemsList.findIndex(i => i.id === id);
    if (index > -1) {
      state.itemsList[index] = state.itemsList[index]
        .set('clientAppearance', appearance)
        .set('status', folder) as Message;
    }
  },
  [fetchHighlightTagsConfig.type]: (state, { payload: { isHighlightTagsEnabled } }) => {
    state.isHighlightTagsEnabled = isHighlightTagsEnabled;
  },
  [approveItemsStart.type]: messageActionReducer,
  [rejectItemsStart.type]: messageActionReducer,
  [approveItemsSuccess.type]: messageActionSuccessReducer,
  [rejectItemsSuccess.type]: messageActionSuccessReducer,
  [approveItemsFailure.type]: messageActionFailureReducer,
  [rejectItemsFailure.type]: messageActionFailureReducer,
  [fetchMessageTagsSpansSuccess.type]: fetchMessageTagsSpansSuccessReducer,
  [searchModerationItemsSuccess.type]: (state, { payload: { searchType, searchValue, userId } }) => {
    // Username and Email search types => ViewType.USER
    let newFilters = { ...defaultAdvancedFilters };
    let viewType = ViewType.USER;

    // Text search type => ViewType.TEXT
    if (searchType === ModerationSearchType.TEXT) {
      viewType = ViewType.TEXT;
      newFilters.text = searchValue;
      setPageInfo(state, { sortBy: Sorting.RELEVANCE });
    } else {
      setPageInfo(state, { sortBy: Sorting.NEWEST });
    }

    if (userId) {
      newFilters.user = userId;
    }
    if (searchType === ModerationSearchType.MESSAGE_ID) {
      viewType = ViewType.MESSAGE_ID;
      newFilters.message_id = searchValue;
    }
    state.isLoading = true;
    state.isModerationMode = false;
    setPageInfo(state, { viewType });
    setAdvancedFilters(state, newFilters);
  },
  [searchModerationItemsFailure.type]: (state, { payload: { searchType, searchValue, userId, error } }) => {
    // In order to reset the folder count and other filters
    resetState(state);

    state.isEmptyResults = true;
  },
  [changePageFilters.type]: (state, { payload: { filters = defaultAdvancedFilters, preventOffsetReset } }) => {
    setAdvancedFilters(state, filters, preventOffsetReset);
    setViewTypeByFilters(state, filters);
    state.isModerationMode = false;
    state.isLoading = true;
  },
  [toggleModerationModeAction.type]: (state, { payload: { value } }) => {
    state.isModerationMode = value ?? !state.isModerationMode;
    setPageInfo(state, { offset: 0 });
    state.isLoading = true;

    if (!state.isModerationMode) {
      state.lockedItems = [];
      state.sessionModeratedMessageCount = 0;
    }
  },
  [setHasMorePending.type]: (state, { payload }) => {
    state.hasMorePending = payload;
  },
  [setHasMorePendingStatus.type]: (state, { payload }) => {
    state.hasMorePendingStatus = payload;
  }
});

function messageActionReducer(state: IModerationMessagesState, { payload: { messageIds, appearance, folder } }) {
  state.itemsList = state.itemsList.map(item =>
    messageIds.includes(item.get('id'))
      ? (item.set('status', folder).set('clientAppearance', appearance) as Message)
      : item
  );
}

function messageActionFailureReducer(state: IModerationMessagesState, { payload: { error, messageIds } }) {
  const {
    pageInfo: { folder },
    itemsOnPage
  } = state;

  state.itemsList = state.itemsList
    .map((item: Message) => {
      const isFailedMessage = messageIds.includes(item.get('id'));
      return !isFailedMessage
        ? item
        : (item.set('status', folder).set('clientAppearance', Appearance.NORMAL) as Message);
    })
    .slice(0, itemsOnPage);
  state.isLoading = false;
}

function messageActionSuccessReducer(state: IModerationMessagesState, { payload: { messageIds, appearance, folder } }) {
  const draftListItems = [...state.itemsList];

  state.sessionModeratedMessageCount += messageIds.length;

  messageIds.forEach(messageId => {
    const messageIndex = state.itemsList.findIndex(item => item.id === messageId);

    if (messageIndex > -1) {
      const message = state.itemsList[messageIndex];

      if (message.status === 'pending' || state.pageInfo.folder === Folder.PENDING) {
        state.counts.pending--;
      }

      draftListItems[messageIndex] = message.set('status', folder).set('clientAppearance', appearance);

      // this.setExpirationTime(true);
    }
  });

  state.itemsList = draftListItems.map((item: Message) => updateItemSelectionState(item, false));
  state.activeItems = calculateActiveItems(state);
  // this.setExpirationTime(this.activeItems > 0);
}

function fetchMessageTagsSpansSuccessReducer(
  state: IModerationMessagesState,
  { payload: { messageId, tagName, spanScoreResults, isEmpty } }
) {
  state.itemsList = state.itemsList.map((message: Message) => {
    const isMessageChecked = message.isChecked();
    if (message.get('id') === messageId) {
      const highlightedTagSpans = message.get('highlightedTagSpans', {});

      if (isEmpty) {
        return updateItemHighlightTagsState(
          message,
          {
            spanScoreResults: spanScoreResults,
            selectedTagName: tagName
          },
          isMessageChecked
        );
      }

      if (highlightedTagSpans?.selectedTagName !== tagName) {
        return updateItemHighlightTagsState(
          message,
          {
            ...spanScoreResults,
            selectedTagName: tagName
          },
          isMessageChecked
        );
      } else {
        return updateItemHighlightTagsState(message, {}, isMessageChecked);
      }
    }
    return message;
  });
}

function updateItemHighlightTagsState(message: Message, newValue: Object, isChecked: boolean): Message {
  let draftMessage = message.set('highlightedTagSpans', newValue) as Message;
  draftMessage.setChecked(isChecked);
  return draftMessage;
}

function updateItemSelectionState(item: Message, checked: boolean) {
  item.setChecked(checked);
  return item;
}

function setItems(state, { folder, messages = [], expirationTime = '' }) {
  if (folder === Folder.PENDING) {
    setPendingItems(state, { folder, messages, expirationTime });
  } else {
    state.itemsList = messages ? messages.slice(0, state.itemsOnPage).map(message => new Message().parse(message)) : [];
  }
  state.activeItems = calculateActiveItems(state);
}

function isSelectedItemExists(state) {
  return (
    state.pageInfo.folder !== Folder.ALL &&
    state.itemsList.filter(item => item.isChecked() && item.status === state.pageInfo.folder).length > 0
  );
}

function setPendingItems(state, { folder, messages = [], expirationTime }) {
  cleanCollapsedItems(state);

  const maxItemsToLoad = getTotalItemsPerPage(state);
  const newItems = [];
  const shouldUpdateExpirationTime = !state.expirationTime && expirationTime && state.isModerationMode;

  /**
   * Crete message map to use it as
   * `Message` instance cache
   */
  const currentItemsMap = new Map(
    (state.itemsList as Message[]).map(function (item) {
      return [item.get('id') as string, item as Message];
    })
  );

  /**
   * Fulfill new items list using cache
   * for existing messages
   */
  messages.forEach(function (message) {
    if (newItems.length <= maxItemsToLoad) {
      newItems.push(currentItemsMap.get(message.id) ?? new Message().parse(message, expirationTime));
    }
  });

  // Set the current expiration time in case we're on moderation mode
  if (!shouldUpdateExpirationTime) {
    state.expirationTime = expirationTime;
    // setExpirationTime(state, expirationTime);
  }

  state.itemsList = newItems;
}

function calculateActiveItems(state) {
  return state.itemsList.reduce(
    (acc, curr) => (curr.clientAppearance === Appearance.NORMAL ? (acc = acc + 1) : acc),
    0
  );
}

function cleanCollapsedItems(state) {
  state.itemsList = state.itemsList.filter(item => item.get('clientAppearance') !== Appearance.COLLAPSED);
}

function getTotalItemsPerPage(state) {
  return state.itemsOnPage;
}

function setPageInfo(state, pageInfo: Partial<IPageInfo>) {
  if (pageInfo?.tag) {
    state.pageInfo.tag = pageInfo.tag;
  }
  const hasNext: boolean = getTotalFolderCount(state) > state.itemsList.length;
  state.pageInfo = merge({}, state.pageInfo, pageInfo, { hasNext });
}

function setAdvancedFilters(state, filters: Partial<IAdvancedFilters>, preventOffsetReset = false) {
  state.filters = merge({}, defaultAdvancedFilters, filters);

  if (!preventOffsetReset) {
    setPageInfo(state, { offset: 0 });
  }
}

function getTotalFolderCount(state) {
  return state.counts[state.pageInfo.folder];
}

function setLastFetch(state) {
  state.lastFetch = Date.parse(new Date().toUTCString()) / 1000;
}

function resetState(state, options = {}) {
  state.itemsList = [];
  state.isMultiSelect = false;
  state.expirationTime = '';
  state.isEmptyResults = false;
  state.counts = defaultCounts;
}

function setViewTypeByFilters(state, filters: IAdvancedFilters) {
  const { user, ip, conversation, text, message_id } = filters;
  const newPageInfo = { ...state.pageInfo };

  if (user) {
    newPageInfo.viewType = ViewType.USER;
  } else if (text) {
    newPageInfo.viewType = ViewType.TEXT;
  } else if (message_id) {
    newPageInfo.viewType = ViewType.MESSAGE_ID;
  } else if (ip) {
    newPageInfo.viewType = ViewType.IP;
  } else if (conversation) {
    newPageInfo.viewType = ViewType.CONVERSATION;
  }

  state.pageInfo = newPageInfo;
}

function getUnpublishedMessagesCount(messagesList: Array<any>): number {
  const count = messagesList.filter(message => !message.get('published')).length;
  return count;
}

function getUnpublishedMessagesOffset(offset: number, state: IModerationMessagesState): number {
  let newUnpublishedMessagesOffset = 0;
  if (offset > state.pageInfo.offset) {
    newUnpublishedMessagesOffset = state.unpublishedMessagesOffset + getUnpublishedMessagesCount(state.itemsList);
  }
  if (offset < state.pageInfo.offset) {
    newUnpublishedMessagesOffset = Math.max(
      0,
      state.unpublishedMessagesOffset - getUnpublishedMessagesCount(state.itemsList)
    );
  }
  return newUnpublishedMessagesOffset;
}

// ---------- State Selectors ---------- //

export function getPageInfo(state: State) {
  return state?.moderation?.messages?.pageInfo || defaultPageInfo;
}

export function getAllUsersFromIP(state: State, ip: string): string[] {
  return state?.moderation?.messages?.itemsList
    ?.filter((message: Message) => message.get('ip') === ip)
    .map((message: Message) => message.get('userId'));
}

export { moderationReducer, TModerationReducer };
